import { inject, Service } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { Observable, of } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { Api } from '@app/api';
import { IamBackendService } from '@core/providers/backend/iam-backend.service';
import { PermissionsBackendService } from '@core/providers/backend/permissions-backend.service';
import { NavigationService } from '@core/providers/navigation/navigation.service';
import { NotificationService } from '@core/providers/notification/notification.service';
import { ContextUtil } from '@core/utils';

@Service()
export class HasNoAccessToFbiGuard {
  private permissionsBackendService = inject(PermissionsBackendService);
  private navigationService = inject(NavigationService);
  private notificationService = inject(NotificationService);
  private iamBackendService = inject(IamBackendService);
  private contextUtil = inject(ContextUtil);

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    // if a user that has access tries to reach page /no-access, this guard will send them to 404 page
    return this.permissionsBackendService.hasAccess$().pipe(
      mergeMap((hasAccess: boolean) => {
        if (hasAccess) {
          return this.iamBackendService.getUserRedirectInfo().pipe(
            mergeMap(response => {
              if (response && response.uuid) {
                this.redirectUserToProperPage(response.type, response.uuid);
              } else {
                this.navigationService.goToInner404();
              }

              this.notificationService.hideLoadingPopup();

              return of(false);
            })
          );
        }

        this.notificationService.hideLoadingPopup();

        return of(!hasAccess);
      })
    );
  }

  private redirectUserToProperPage(
    type: Api.iam.auth.defaultItem.GetResult['type'],
    uuid: string
  ): void {
    if (type === null) {
      return;
    }
    if (this.contextUtil.isVenueContext(type)) {
      this.navigationService.goToVenueMainScreen(uuid);
    } else if (this.contextUtil.isAccountContext(type)) {
      this.navigationService.goToScoreboards(uuid);
    }
  }
}
